import { useMemo } from 'react';
import { Rocket, Cpu, Play, CheckCircle, XCircle, Trash2, Zap, FlaskConical, GitCommit, Package, Wrench } from 'lucide-react';
import type { TelemetryEvent } from '../../api/telemetry';

interface SprintLifecycleTimelineProps {
  events: TelemetryEvent[];
  className?: string;
}

interface TimelineNode {
  id: string;
  eventType: string;
  timestamp: string;
  label: string;
  color: string;
  bgColor: string;
  icon: React.ReactNode;
  issueId?: string | null;
  detail?: string;
}

const EVENT_CONFIG: Record<string, { label: string; color: string; bgColor: string; icon: React.ReactNode }> = {
  workflow_started:               { label: 'Started',     color: 'text-blue-600',    bgColor: 'bg-blue-100',    icon: <Rocket size={12} /> },
  wave_plan_computed:             { label: 'Wave Plan',   color: 'text-blue-600',    bgColor: 'bg-blue-100',    icon: <Zap size={12} /> },
  sprite_spawned:                 { label: 'Sprite Up',   color: 'text-coral-600',   bgColor: 'bg-coral-100',   icon: <Cpu size={12} /> },
  story_execution_started:        { label: 'Build',       color: 'text-coral-600',   bgColor: 'bg-coral-100',   icon: <Play size={12} /> },
  story_execution_completed:      { label: 'Built',       color: 'text-accent-600',  bgColor: 'bg-accent-100',  icon: <CheckCircle size={12} /> },
  verification_completed:         { label: 'Verified',    color: 'text-accent-600',  bgColor: 'bg-accent-100',  icon: <CheckCircle size={12} /> },
  sprite_destroyed:               { label: 'Sprite Down', color: 'text-neutral-500', bgColor: 'bg-neutral-100', icon: <Trash2 size={12} /> },
  test_wave_started:              { label: 'Test Wave',   color: 'text-blue-600',    bgColor: 'bg-blue-100',    icon: <FlaskConical size={12} /> },
  test_issue_execution_started:   { label: 'Test Run',    color: 'text-blue-600',    bgColor: 'bg-blue-100',    icon: <FlaskConical size={12} /> },
  commit_made:                    { label: 'Commit',             color: 'text-blue-600',    bgColor: 'bg-blue-100',    icon: <GitCommit size={12} /> },
  batch_execution_completed:      { label: 'Batch Done',         color: 'text-accent-600',  bgColor: 'bg-accent-100',  icon: <CheckCircle size={12} /> },
  agent_started:                  { label: 'Agent',              color: 'text-coral-600',   bgColor: 'bg-coral-100',   icon: <Play size={12} /> },
  agent_completed:                { label: 'Agent Done',         color: 'text-accent-600',  bgColor: 'bg-accent-100',  icon: <CheckCircle size={12} /> },
  // Full-cycle phase events
  build_started:                  { label: 'Build Started',      color: 'text-orange-600',  bgColor: 'bg-orange-100',  icon: <Package size={12} /> },
  build_completed:                { label: 'Build Completed',    color: 'text-accent-600',  bgColor: 'bg-accent-100',  icon: <Package size={12} /> },
  test_started:                   { label: 'E2E Tests Started',  color: 'text-blue-600',    bgColor: 'bg-blue-100',    icon: <FlaskConical size={12} /> },
  repair_started:                 { label: 'Repair Started',     color: 'text-yellow-600',  bgColor: 'bg-yellow-100',  icon: <Wrench size={12} /> },
  repair_completed:               { label: 'Repair Completed',   color: 'text-accent-600',  bgColor: 'bg-accent-100',  icon: <Wrench size={12} /> },
};

// Exclude noisy events
const EXCLUDED_TYPES = new Set(['sprite_heartbeat', 'claude_tool_use']);

function fmtTime(ts: string): string {
  return new Date(ts).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
}

export default function SprintLifecycleTimeline({ events, className }: SprintLifecycleTimelineProps) {
  const nodes = useMemo<TimelineNode[]>(() => {
    return events
      .filter((e) => !EXCLUDED_TYPES.has(e.eventType) && EVENT_CONFIG[e.eventType])
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
      .map((e) => {
        const cfg = EVENT_CONFIG[e.eventType];
        // Mark failed verifications red
        const isFailed = e.eventType === 'verification_completed' && (e.payload as Record<string, unknown>)?.passed === false;
        const isError = e.eventType === 'story_execution_completed' && (e.payload as Record<string, unknown>)?.result === 'failure';
        return {
          id: e.id,
          eventType: e.eventType,
          timestamp: e.timestamp,
          label: cfg.label,
          color: isFailed || isError ? 'text-red-600' : cfg.color,
          bgColor: isFailed || isError ? 'bg-red-100' : cfg.bgColor,
          icon: isFailed || isError ? <XCircle size={12} /> : cfg.icon,
          issueId: e.issueId,
          detail: e.issueId ? e.issueId : undefined,
        };
      });
  }, [events]);

  if (nodes.length === 0) {
    return null;
  }

  return (
    <div className={`bg-white rounded-lg border border-neutral-200 shadow-warm-sm p-4 ${className || ''}`}>
      <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400 mb-3">Lifecycle</p>
      <div className="overflow-x-auto">
        <div className="flex items-center gap-1 min-w-max py-1">
          {nodes.map((node, i) => (
            <div key={node.id} className="flex items-center">
              {/* Node */}
              <div className="group relative flex flex-col items-center" style={{ minWidth: 40 }}>
                <div className={`flex items-center justify-center w-7 h-7 rounded-full ${node.bgColor} ${node.color} transition-transform group-hover:scale-110`}>
                  {node.icon}
                </div>
                <span className={`text-[8px] font-semibold mt-1 whitespace-nowrap ${node.color}`}>
                  {node.label}
                </span>
                {/* Tooltip on hover */}
                <div className="absolute bottom-full mb-2 hidden group-hover:block z-10">
                  <div className="bg-neutral-800 text-white text-[9px] rounded px-2 py-1 whitespace-nowrap shadow-lg">
                    <div className="font-semibold">{node.eventType}</div>
                    <div className="text-neutral-400">{fmtTime(node.timestamp)}</div>
                    {node.detail && <div className="text-neutral-300 font-mono">{node.detail}</div>}
                  </div>
                </div>
              </div>
              {/* Connector line */}
              {i < nodes.length - 1 && (
                <div className="w-4 h-px bg-neutral-300 shrink-0" />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
